import React from "react";
import { ChevronDown, Check, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface MusicSettings {
  customMode: boolean;
  instrumental: boolean;
  model: string;
  title: string;
  tags: string;
  negativeTags: string;
  lyrics: string;
}

interface MusicQuickSettingsProps {
  settings: MusicSettings;
  onSettingsChange: (settings: MusicSettings) => void;
  hasChangedSettings: boolean;
  onUseDefaults: () => void;
}

export function MusicQuickSettings({
  settings,
  onSettingsChange,
  hasChangedSettings,
  onUseDefaults,
}: MusicQuickSettingsProps) {
  const updateSetting = (key: keyof MusicSettings, value: any) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  const modelOptions = [
    { value: "chirp-v5", label: "Suno V5" },
    { value: "minimax-1.5", label: "Minimax 1.5" },
  ];

  const modeOptions = [
    { value: false, label: "Simple (AI writes lyrics)" },
    { value: true, label: "Custom (I write lyrics)" },
  ];

  const vocalOptions = [
    { value: false, label: "With Vocals" },
    { value: true, label: "Instrumental Only" },
  ];

  const buttonClass = "rounded-full bg-secondary hover:bg-secondary/80 text-secondary-foreground px-2.5 md:px-4 py-1.5 md:py-2 h-8 md:h-9 text-xs md:text-sm font-medium";

  const getModelLabel = () => {
    const option = modelOptions.find(opt => opt.value === settings.model);
    return option?.label || "Suno V5";
  };

  const getModeLabel = () => {
    return settings.customMode ? "Custom" : "Simple";
  };

  const getVocalLabel = () => {
    return settings.instrumental ? "Instrumental" : "With Vocals";
  };

  return (
    <div className="flex flex-wrap items-end justify-center gap-2 md:gap-4">
      {/* Model */}
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs text-muted-foreground">Model</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" className={buttonClass}>
              {getModelLabel()} <ChevronDown className="ml-1.5 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-popover border-border">
            {modelOptions.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                onClick={() => updateSetting("model", opt.value)}
                className="flex items-center justify-between"
              >
                {opt.label}
                {settings.model === opt.value && <Check className="h-4 w-4 ml-2" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Mode */}
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs text-muted-foreground">Mode</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" className={buttonClass}>
              {getModeLabel()} <ChevronDown className="ml-1.5 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-popover border-border">
            {modeOptions.map((opt) => (
              <DropdownMenuItem
                key={String(opt.value)}
                onClick={() => updateSetting("customMode", opt.value)}
                className="flex items-center justify-between"
              >
                {opt.label}
                {settings.customMode === opt.value && <Check className="h-4 w-4 ml-2" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Vocals */}
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs text-muted-foreground">Vocals</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" className={buttonClass}>
              {getVocalLabel()} <ChevronDown className="ml-1.5 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-popover border-border">
            {vocalOptions.map((opt) => (
              <DropdownMenuItem
                key={String(opt.value)}
                onClick={() => updateSetting("instrumental", opt.value)}
                className="flex items-center justify-between"
              >
                {opt.label}
                {settings.instrumental === opt.value && <Check className="h-4 w-4 ml-2" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Reset Button */}
      {hasChangedSettings && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onUseDefaults}
          className="rounded-full h-9 px-3 text-muted-foreground hover:text-foreground"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
